import { useEffect, useMemo, useState } from "react";
import { CarFront, BookOpen, Moon, RefreshCw, Sun, Volume2 } from "lucide-react";
import type { CommuteSnapshot, SimulationOverrides } from "../../shared/types";
import { CHIME_LABELS, CHIME_TIERS, playChime } from "../chime";
import { isNativeShell, postHaptic, postReminder, shellBuild, shellVersion } from "../native";
import { prepareSpeech, speak, testVoice } from "../speech";
import { readSpeechVoice, SPEECH_VOICE_CHOICES, SPEECH_VOICE_ORDER } from "../../shared/speech-catalogue";
import { BUILD_ID, BUILD_TAG, builtAtLabel } from "../build";
import { clock, minutes, relativeAge } from "../../core/format";
import { stationLabel } from "../../shared/labels";
import { adminLoginForDevice } from "../../core/admin-logins";
import { ActivityPreview } from "./ActivityPreview";
import type { NativeJourney } from "../../shared/native-bridge";
import { wazeNavigationUrl, type NavTarget } from "../navigation";
import { sendReport } from "../report";
import { homeForStation, matchOriginPreset } from "../../shared/origins";
import { SIMULATED_LOCATIONS } from "../../shared/origins";
import { PICKUP_ZONES } from "../../core/pickup-map";
import type { useMapTest } from "../useMapTest";
import { MapTest } from "./MapTest";

/** The condition overrides went unused once standing somewhere by hand could do the same job. */
const SHOW_SIMULATION_CONTROLS = false;

/** Dropping the key rather than blanking it, so an empty override does not read as a live one. */
function withoutPosition(simulation: SimulationOverrides | null): SimulationOverrides | null {
	if (!simulation) {
		return null;
	}
	const { position, ...rest } = simulation;
	void position;
	return Object.keys(rest).length > 0 ? rest : null;
}
import { useAlertDiagnostics } from "../alertDiagnostics";
import { useSheetDismiss } from "../useSheetDismiss";
import { ScrollTopButton } from "./ScrollTopButton";
import { toggleWakeLock } from "../useWakeLock";
import type { Preferences } from "../usePreferences";

interface SettingsProps {
	mapTest: ReturnType<typeof useMapTest>;
	snapshot: CommuteSnapshot | null;
	preferences: Preferences;
	deviceId: string;
	/** Which platform he is getting off at, which on the way back settles which house is home. */
	destinationStopId: string | null;
	position: { lat: number; lon: number } | null;
	/** Exactly what the shell is sent, so the preview cannot drift from the thing it previews. */
	nativeJourney: NativeJourney;
	nowMs: number;
	onChange: (patch: Partial<Preferences>) => void;
	onOpenGuide: () => void;
	onClose: () => void;
}

const TEXT_SCALES = [1, 1.15, 1.3, 1.5];

export function SettingsSheet({ snapshot, preferences, deviceId, destinationStopId, position, nativeJourney, nowMs, mapTest, onChange, onOpenGuide, onClose }: SettingsProps) {
	const nativeShell = isNativeShell();
	const haptics = preferences.hapticIntensity ?? 1;
	/*
	 * Out to the platform, back to the house.
	 *
	 * The two legs want opposite ends of the same drive, and the ride home has no boarding station
	 * worth driving to — he is already standing on it. Which house is settled by the platform he
	 * is getting off at, falling back to whichever is nearer when he has not chosen yet.
	 */
	const routeTarget = useMemo((): NavTarget | null => {
		if (!snapshot) {
			return null;
		}
		if (snapshot.profile.hasAccessLeg) {
			const station = snapshot.profile.boardingStation;
			return { ...station, name: stationLabel(station.name) };
		}
		const home = homeForStation(destinationStopId, position);
		return home ? { id: home.id, name: home.label, lat: home.lat, lon: home.lon, address: home.address } : null;
	}, [snapshot, destinationStopId, position]);
	const simulation = preferences.simulation;
	const textScale = preferences.textScale ?? 1;
	const dismiss = useSheetDismiss(onClose);
	const [testStatus, setTestStatus] = useState<string | null>(null);
	const [lockStatus, setLockStatus] = useState<string | null>(null);
	const [spokenIn, setSpokenIn] = useState<number | null>(null);
	const [noticeIn, setNoticeIn] = useState<number | null>(null);
	const [pickupStatus, setPickupStatus] = useState<string | null>(null);

	// The two ways out that do not run through the pickup screen. Completing one there means
	// pressing "I'm here" first, which buzzes Eben; cancelling is only offered from inside it.
	const actOnPickup = async (action: "finish" | "reset", done: string) => {
		setPickupStatus("Working…");
		try {
			const response = await fetch("/api/pickup", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ deviceId, action }),
			});
			if (!response.ok) {
				throw new Error("That did not work.");
			}
			const body = (await response.json()) as { pickup: unknown };
			setPickupStatus(body.pickup ? done : "There is no pickup today.");
		} catch (cause) {
			setPickupStatus(cause instanceof Error ? cause.message : "That did not work.");
		}
	};

	// Delayed sends prove the notification arrives with the app closed, which is the case that matters.
	const sendTest = async (delaySeconds: number) => {
		setTestStatus("Sending…");
		try {
			const response = await fetch("/api/alerts/test", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ deviceId, delaySeconds }),
			});
			if (!response.ok) {
				const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
				throw new Error(body?.error?.message ?? "Test failed.");
			}
			setTestStatus(delaySeconds === 0 ? "Sent — it should arrive in a moment." : `Scheduled for ${delaySeconds} seconds from now.`);
		} catch (cause) {
			setTestStatus(cause instanceof Error ? cause.message : "Test failed.");
		}
	};

	return (
		<div className="sheet" role="dialog" aria-label="Settings">
			<div className="sheet__scrim" onClick={onClose} />
			<div
				className="sheet__body"
				ref={dismiss.bodyRef}
				style={dismiss.style}
				{...dismiss.handlers}
			>
				<span className="sheet__grip" aria-hidden="true" />
				<ScrollTopButton targetRef={dismiss.bodyRef} className="sheet__toTop" />
				<header className="sheet__head">
					<h2>Settings</h2>
					<button type="button" onClick={onClose} aria-label="Close">
						✕
					</button>
				</header>

				<button type="button" className="sheet__guideLink" onClick={onOpenGuide}>
					<BookOpen size={16} aria-hidden="true" />
					How to use GoTime
				</button>

				{routeTarget ? (
					<a
						className="sheet__guideLink sheet__routeLink"
						href={wazeNavigationUrl(routeTarget)}
						target="_blank"
						rel="noreferrer"
					>
						<CarFront size={16} aria-hidden="true" />
						Route me to {routeTarget.name}
					</a>
				) : null}

				<Field label="Appearance">
					<div className="appearance__modes" role="group" aria-label="Color mode">
						{(["dark", "light"] as const).map((mode) => (
							<button key={mode} type="button" aria-pressed={preferences.colorMode === mode} onClick={() => onChange({ colorMode: mode })}>
								{mode === "dark" ? <Moon size={18} aria-hidden="true" /> : <Sun size={18} aria-hidden="true" />}
								{mode === "dark" ? "Dark" : "Light"}
							</button>
						))}
					</div>
					<label className="appearance__glass">
						<span>Liquid glass <span className="appearance__badge">Preview</span></span>
						<input type="checkbox" role="switch" aria-label="Liquid glass" checked={preferences.liquidGlass} onChange={(event) => onChange({ liquidGlass: event.target.checked })} />
					</label>
				</Field>

				<Field label="Text size" hint="Scales the alerts, train list and details. The big countdown stays as it is.">
					<div className="picker__options">
						{TEXT_SCALES.map((scale) => (
							<button
								key={scale}
								type="button"
								className={`chip ${scale === textScale ? "is-active" : ""}`}
								aria-pressed={scale === textScale}
								onClick={() => onChange({ textScale: scale })}
							>
								{Math.round(scale * 100)}%
							</button>
						))}
					</div>
				</Field>

				<Field
					label="Spoken alerts"
					hint="Sounds a chime and speaks while GoTime is open. Notification sound is controlled in iOS Settings."
				>
					<label className="sheet__toggle">
						<input type="checkbox" checked={preferences.soundEnabled} onChange={(event) => onChange({ soundEnabled: event.target.checked })} />
						Enabled
					</label>
					<div className="picker__options">
						{CHIME_TIERS.map((tier) => (
							<button key={tier} type="button" className="chip" onClick={() => playChime(tier)}>
								{CHIME_LABELS[tier]}
							</button>
						))}
					</div>
				</Field>

				<Field label="Reminder voice" hint="The words are generated ahead of time and played from a clip, which is the only way iOS will speak from a timer.">
					<div className="sheet__voice">
						<select
							aria-label="Reminder voice"
							value={preferences.speechVoice}
							onChange={(event) => onChange({ speechVoice: readSpeechVoice(event.target.value) })}
						>
							{SPEECH_VOICE_ORDER.map((voice) => (
								<option key={voice} value={voice}>
									{SPEECH_VOICE_CHOICES[voice].label}
								</option>
							))}
						</select>
						<button type="button" className="sheet__voice-test" onClick={() => void testVoice(deviceId, preferences.speechVoice)}>
							<Volume2 size={17} strokeWidth={2} aria-hidden="true" />
							Test voice
						</button>
					</div>
				</Field>

				<Field
					label="Test a reminder later"
					hint="Speaks from a timer rather than from your tap, which is the path a real reminder takes. iOS suspends the page when the screen locks, so leave the app open to hear it."
				>
					<div className="picker__options">
						{[30, 60, 300].map((seconds) => (
							<button
								key={seconds}
								type="button"
								className="chip"
								onClick={() => {
									setSpokenIn(seconds);
									// Fetched on the tap, played by the timer: exactly how a real reminder is staged.
									void prepareSpeech(deviceId, { useCase: "test" }, preferences.speechVoice);
									window.setTimeout(() => speak({ useCase: "test" }, preferences.speechVoice, "go"), seconds * 1000);
								}}
							>
								{seconds < 60 ? `${seconds} sec` : `${seconds / 60} min`}
							</button>
						))}
					</div>
					{spokenIn ? <p className="field__hint">Queued. It will speak in {spokenIn < 60 ? `${spokenIn} seconds` : `${spokenIn / 60} minutes`}.</p> : null}
				</Field>

				<Field label="Test alerts" hint="Sends the real notification for your saved alert, so a test proves the whole chain.">
					<div className="picker__options">
						<button type="button" className="chip" onClick={() => void sendTest(0)}>
							Send now
						</button>
						<button type="button" className="chip" onClick={() => void sendTest(15)}>
							Send in 15 sec
						</button>
					</div>
					{testStatus ? <p className="field__hint">{testStatus}</p> : null}
				</Field>

				<Field label="Screen lock" hint="Held automatically from ten minutes before the first reminder until the leave time. Tap to test it now.">
					<div className="picker__options">
						<button type="button" className="chip" onClick={() => void toggleWakeLock().then(setLockStatus)}>
							Hold or release
						</button>
					</div>
					{lockStatus ? <p className="field__hint">{lockStatus}</p> : null}
				</Field>

				{adminLoginForDevice(deviceId) != null ? (
					<Field
						label="Pickup state"
						hint={
							adminLoginForDevice(deviceId) === "david"
								? "Neither tells Eben anything. Done closes today off; reset un-starts it, so his phone can raise a fresh one this afternoon."
								: "Neither tells your ride anything. Done closes today off; reset un-starts it, so you can ask for a lift again."
						}
					>
						<div className="picker__options">
							<button type="button" className="chip" onClick={() => void actOnPickup("finish", "Done for today.")}>
								Done for today
							</button>
							<button type="button" className="chip" onClick={() => void actOnPickup("reset", "Reset. It is an open offer again.")}>
								Reset pickup state
							</button>
						</div>
						{pickupStatus ? <p className="field__hint">{pickupStatus}</p> : null}
					</Field>
				) : null}

				<Field
					label="Haptics"
					hint={
						nativeShell
							? "The buzz when a phase is declared. iOS has no haptic volume of its own, so this scales the strength GoTime asks for."
							: "Only the iOS app can buzz — Safari has no vibration on iPhone. Open GoTime in the app to test these."
					}
				>
					<label className="sheet__stacked">
						<span>Strength — {haptics === 0 ? "off" : `${Math.round(haptics * 100)}%`}</span>
						<input
							type="range"
							min={0}
							max={1}
							step={0.05}
							value={haptics}
							disabled={!nativeShell}
							onChange={(event) => onChange({ hapticIntensity: Number(event.target.value) })}
						/>
					</label>
					<div className="picker__options">
						<button type="button" className="chip" disabled={!nativeShell} onClick={() => postHaptic("selection")}>
							Selection
						</button>
						<button type="button" className="chip" disabled={!nativeShell} onClick={() => postHaptic("impact")}>
							Impact
						</button>
						<button type="button" className="chip" disabled={!nativeShell} onClick={() => postHaptic("warning")}>
							Warning
						</button>
						<button type="button" className="chip" disabled={!nativeShell} onClick={() => postHaptic("alert")}>
							Vibrate
						</button>
					</div>
					{/* The one buzz the slider cannot touch, so it must not look like it can. */}
					<p className="field__hint">Vibrate is the system alert and always fires at full strength.</p>
				</Field>

				<Field
					label="Lock screen preview"
					hint="Draws the Live Activity and Dynamic Island from the exact payload the iOS app is sent, so the layout can be worked on without rebuilding. iOS renders the real ones."
				>
					<label className="sheet__toggle">
						<input
							type="checkbox"
							checked={preferences.activityPreview}
							onChange={(event) => onChange({ activityPreview: event.target.checked })}
						/>
						Show preview
					</label>
					{preferences.activityPreview ? <ActivityPreview journey={nativeJourney} nowMs={nowMs} /> : null}
				</Field>

				<Field
					label="Locked screen notice"
					hint={
						nativeShell
							? "A local notification, scheduled on the phone. It fires with the app closed but cannot revise itself — a train that slips still fires at the old time. Only a push can correct that."
							: "Only the iOS app can do this. In Safari the notification is web push, which is what Test alerts uses."
					}
				>
					<div className="picker__options">
						{[30, 120].map((seconds) => (
							<button
								key={seconds}
								type="button"
								className="chip"
								disabled={!nativeShell}
								onClick={() => {
									setNoticeIn(seconds);
									postReminder({
										id: "test",
										title: "GoTime",
										body: "Locked screen test. The app raised this without web push.",
										atMs: Date.now() + seconds * 1000,
									});
								}}
							>
								{seconds < 60 ? `${seconds} sec` : `${seconds / 60} min`}
							</button>
						))}
					</div>
					{noticeIn ? <p className="field__hint">Queued. Lock the phone now — it should arrive in {noticeIn} seconds.</p> : null}
				</Field>

				<Field
					label="Location override"
					hint="Stands in for the phone's own location, so a morning that starts at the other house can be tried from here."
				>
					<div className="picker__options">
						<button
							type="button"
							className={`chip ${simulation?.position == null ? "is-active" : ""}`}
							onClick={() => onChange({ simulation: withoutPosition(simulation) })}
						>
							Use my location
						</button>
						{[...SIMULATED_LOCATIONS, ...PICKUP_ZONES.map(zone => ({ id: zone.id, label: zone.name, lat: zone.boundary.reduce((sum, point) => sum + point.lat, 0) / zone.boundary.length, lon: zone.boundary.reduce((sum, point) => sum + point.lon, 0) / zone.boundary.length }))].map((place) => {
							const active =
								simulation?.position != null &&
								Math.abs(simulation.position.lat - place.lat) < 0.0000001 &&
								Math.abs(simulation.position.lon - place.lon) < 0.0000001;
							return (
								<button
									key={place.id}
									type="button"
									className={`chip ${active ? "is-active" : ""}`}
									onClick={() =>
										onChange({
											simulation: active
												? withoutPosition(simulation)
												: { ...simulation, position: { lat: place.lat, lon: place.lon } },
										})
									}
								>
									{place.label}
								</button>
							);
						})}
					</div>
					<p className="field__hint">
						{simulation?.position
							? "The phone's own location is ignored while this is set."
							: "Off — using the phone's location."}
					</p>
				</Field>

				<Field label="Developer">
					<MapTest test={mapTest} deviceId={deviceId} nowMs={Date.now()} colorMode={preferences.colorMode} />
					<label className="sheet__toggle">
						<input type="checkbox" checked={preferences.debug} onChange={(event) => onChange({ debug: event.target.checked })} />
						Show the decision engine's inputs
					</label>
					{SHOW_SIMULATION_CONTROLS ? (
						<label className="sheet__toggle">
							<input
								type="checkbox"
								checked={simulation != null}
								onChange={(event) => onChange({ simulation: event.target.checked ? {} : null })}
							/>
							Simulate conditions
						</label>
					) : null}
				</Field>

				{SHOW_SIMULATION_CONTROLS && simulation ? (
					<Field label="Simulation" hint="Server-side overrides so behaviour can be checked without waiting for 7 AM.">
						<div className="sheet__pair">
							<span>Pretend it is</span>
							<input
								type="time"
								value={simulation.nowMs == null ? "" : toClockInput(minutesOfDay(simulation.nowMs))}
								onChange={(event) => onChange({ simulation: { ...simulation, nowMs: epochForTodayAt(event.target.value) } })}
							/>
							<button type="button" className="picker__reset" onClick={() => onChange({ simulation: { ...simulation, nowMs: undefined } })}>
								Live
							</button>
						</div>
						{simulation.nowMs != null ? (
							<p className="field__hint">
								{new Date(simulation.nowMs).toLocaleString("en-US", { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
								{" — BART runs a different timetable at weekends."}
							</p>
						) : null}

						<label className="sheet__sim">
							Delay every train
							<input
								type="range"
								min={0}
								max={1800}
								step={60}
								value={simulation.delaySeconds ?? 0}
								onChange={(event) => onChange({ simulation: { ...simulation, delaySeconds: Number(event.target.value) || undefined } })}
							/>
							<span>{Math.round((simulation.delaySeconds ?? 0) / 60)} min</span>
						</label>
						<label className="sheet__sim">
							Drive time
							<input
								type="range"
								min={0}
								max={3600}
								step={60}
								value={simulation.driveSeconds ?? 0}
								onChange={(event) => onChange({ simulation: { ...simulation, driveSeconds: Number(event.target.value) || undefined } })}
							/>
							<span>{simulation.driveSeconds ? `${Math.round(simulation.driveSeconds / 60)} min` : "live"}</span>
						</label>
						<label className="sheet__toggle">
							<input
								type="checkbox"
								checked={simulation.disableRealtime === true}
								onChange={(event) => onChange({ simulation: { ...simulation, disableRealtime: event.target.checked || undefined } })}
							/>
							Pretend realtime is down
						</label>
						<label className="sheet__toggle">
							<input
								type="checkbox"
								checked={simulation.disableRouting === true}
								onChange={(event) => onChange({ simulation: { ...simulation, disableRouting: event.target.checked || undefined } })}
							/>
							Pretend routing is down
						</label>
					</Field>
				) : null}

				<button type="button" className="sheet__reload" onClick={() => window.location.reload()}>
					<RefreshCw size={17} strokeWidth={2} aria-hidden="true" />
					Refresh app
				</button>

				<p className="sheet__foot">Preferences stay on this device. Saved alerts and journey records are stored on the server.</p>
			</div>
		</div>
	);
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
	return (
		<section className="field">
			<h3>{label}</h3>
			{hint ? <p className="field__hint">{hint}</p> : null}
			{children}
		</section>
	);
}

/**
 * What the browser thinks the screen is.
 *
 * The footer sitting away from the bottom edge is an iOS-only fault that cannot be reproduced
 * on a desktop, so the numbers behind it are worth reading off the phone itself rather than
 * guessing at another fix.
 */
function Viewport({ nowMs }: { nowMs: number }) {
	const [readings, setReadings] = useState<Array<[string, string]>>([]);

	useEffect(() => {
		const app = document.querySelector(".app");
		const footer = document.querySelector(".statusbar");
		const visual = window.visualViewport;
		const appBox = app?.getBoundingClientRect();
		const footerBox = footer?.getBoundingClientRect();
		const gap = footerBox ? Math.round(window.innerHeight - footerBox.bottom) : null;
		const footerStyle = footer ? getComputedStyle(footer) : null;

		setReadings([
			["Gap below footer", gap == null ? "—" : `${gap}px`],
			// The layout viewport is shorter than the screen on this phone, so measuring the footer
			// against it says "flush" while there is a black band underneath.
			["Gap to screen", footerBox ? `${Math.round(window.screen.height - footerBox.bottom)}px` : "—"],
			// Told apart because they look identical on screen and have opposite fixes.
			["Footer padding", footerStyle ? `${footerStyle.paddingBottom} below the row` : "—"],
			["Footer box", footerBox ? `${Math.round(footerBox.height)}px · bottom ${Math.round(footerBox.bottom)}` : "—"],
			["Window inner", `${window.innerHeight}px`],
			["Screen", `${window.screen.height}px · available ${window.screen.availHeight}px`],
			["Visual viewport", visual ? `${Math.round(visual.height)}px · offset ${Math.round(visual.offsetTop)} · scale ${visual.scale.toFixed(2)}` : "unsupported"],
			["Document client", `${document.documentElement.clientHeight}px`],
			["App box", appBox ? `${Math.round(appBox.height)}px · top ${Math.round(appBox.top)}` : "—"],
			["Page scroll", `${Math.round(window.scrollY)} of ${document.documentElement.scrollHeight - window.innerHeight}px`],
			["Safe area bottom", getComputedStyle(document.documentElement).getPropertyValue("--safe-bottom").trim() || "0px"],
			["Standalone", window.matchMedia("(display-mode: standalone)").matches ? "yes" : "no"],
		]);
	}, [nowMs]);

	return <Grid rows={readings} />;
}

/**
 * iOS reports every iPhone as "iPhone" in the user agent, so the screen is the only tell.
 *
 * Worth naming because the layout faults here are all about viewport height against screen
 * height, and that argument cannot be settled without knowing which screen.
 */
const IPHONE_SCREENS: Record<string, string> = {
	"402x874": "iPhone 16 Pro",
	"440x956": "iPhone 16 Pro Max",
	"393x852": "iPhone 14 Pro / 15 / 16",
	"430x932": "iPhone 15 Plus / 16 Plus",
	"390x844": "iPhone 12 / 13 / 14",
	"428x926": "iPhone 12 / 13 Pro Max",
	"375x812": "iPhone X / 11 Pro / 13 mini",
	"414x896": "iPhone XR / 11",
	"375x667": "iPhone SE",
};

function deviceModel(): string {
	const key = `${window.screen.width}x${window.screen.height}`;
	return IPHONE_SCREENS[key] ?? (/iPhone|iPad/.test(navigator.userAgent) ? `unrecognised iOS ${key}` : "not an iPhone");
}

function readInset(name: string): string {
	return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "0px";
}

function footerMetrics(): { height: number; bottom: number; paddingBottom: string } | null {
	const footer = document.querySelector(".statusbar");
	if (!footer) {
		return null;
	}
	const box = footer.getBoundingClientRect();
	return { height: Math.round(box.height), bottom: Math.round(box.bottom), paddingBottom: getComputedStyle(footer).paddingBottom };
}

/**
 * Which phone this is, and which build it is running.
 *
 * Both are guesses otherwise: the server logs a device by the first three characters of its id,
 * and a service worker will happily keep serving a build from days ago.
 */
function Identity({ deviceId, location }: { deviceId: string; location: LocationDebug }) {
	const diagnostics = useAlertDiagnostics();
	const [result, setResult] = useState<string | null>(null);
	const [sending, setSending] = useState(false);
	const [adminCode, setAdminCode] = useState<string>("none received");

	useEffect(() => {
		const showCode = (code: string, expiresAtMs: number) => {
			setAdminCode(expiresAtMs > Date.now() ? `${code} — expires ${clock(expiresAtMs, Intl.DateTimeFormat().resolvedOptions().timeZone)}` : "expired — request another");
		};
		void caches.match("/__gotime/admin-code").then(async (response) => {
			if (!response) return;
			const value = await response.json() as { code?: string; expiresAtMs?: number };
			if (!value.code || !value.expiresAtMs) return;
			showCode(value.code, value.expiresAtMs);
		});
		const receiveCode = (event: MessageEvent<{ type?: string; code?: string; expiresAtMs?: number }>) => {
			if (event.data?.type === "admin-code-received" && event.data.code && event.data.expiresAtMs) showCode(event.data.code, event.data.expiresAtMs);
		};
		navigator.serviceWorker?.addEventListener("message", receiveCode);
		return () => navigator.serviceWorker?.removeEventListener("message", receiveCode);
	}, []);

	return (
		<>
			<Grid
				rows={[
					// The short form is what appears in the worker logs, and is enough to query on.
					["Device", deviceId.slice(0, 3)],
					["Model", deviceModel()],
					["Screen", `${window.screen.width}×${window.screen.height} @${window.devicePixelRatio}x`],
					["Build", BUILD_TAG],
					["Built", builtAtLabel()],
					["iOS app", shellBuild() ? `${shellBuild()} · bridge v${shellVersion()}` : "no — this is the web app"],
					[
						"Installed",
						shellBuild()
							? "yes — the iOS app"
							: window.matchMedia("(display-mode: standalone)").matches
								? "yes — from the Home Screen"
								: "no — in the browser",
					],
					["Admin code", adminLoginForDevice(deviceId) ? adminCode : "not the delivery device"],
				]}
			/>
			<button
				type="button"
				className="chip"
				disabled={sending}
				onClick={() => {
					setSending(true);
					setResult(null);
					void sendReport("debug-panel", {
						build: BUILD_ID,
						standalone: window.matchMedia("(display-mode: standalone)").matches,
						userAgent: navigator.userAgent,
						screen: {
							width: window.screen.width,
							height: window.screen.height,
							availHeight: window.screen.availHeight,
							pixelRatio: window.devicePixelRatio,
							innerHeight: window.innerHeight,
							clientHeight: document.documentElement.clientHeight,
							visualHeight: window.visualViewport ? Math.round(window.visualViewport.height) : null,
							safeTop: readInset("--safe-top"),
							safeBottom: readInset("--safe-bottom"),
							footer: footerMetrics(),
						},
						location,
						diagnostics,
					})
						.then(setResult)
						.finally(() => setSending(false));
				}}
			>
				{sending ? "Sending…" : "Send diagnostics"}
			</button>
			<p className="field__hint">
				{result == null
					? "Diagnostics are only recorded for a device with a saved alert. This says whether yours is one of them."
					: result === "sent"
						? "Sent — the last day of events is on the server."
						: `Not recorded: ${result}`}
			</p>
		</>
	);
}

/**
 * Why a reminder did or did not speak.
 *
 * Reads state the app already keeps and never writes any back, so watching this cannot change
 * what the reminders do.
 */
interface LocationDebug {
	watching: boolean;
	pending: boolean;
	error: string | null;
	accuracyMeters: number | null;
	/** A position that has stopped updating reads exactly like a correct one without this. */
	fixAtMs: number | null;
	speedMetersPerSecond: number | null;
	traveling: boolean;
	driving: boolean;
	mode: string;
	metersFromHome: number | null;
	/** Which of the two houses the phone is standing at, which forbids drive mode. */
	atHomeLabel: string | null;
	forced: boolean;
	/** Whether the drive is being routed from the phone rather than the saved address. */
	routingFromCar: boolean;
}

function VoiceAlerts({
	leads,
	leaveByMs,
	nowMs,
	soundEnabled,
	deviceId,
}: {
	leads: number[];
	leaveByMs: number | null;
	nowMs: number;
	soundEnabled: boolean;
	deviceId: string;
}) {
	const diagnostics = useAlertDiagnostics();
	const server = useAlertSchedule(deviceId);
	const pending = leaveByMs == null ? [] : [...leads].sort((a, b) => b - a).map((minutesBefore) => ({ minutesBefore, dueMs: leaveByMs - minutesBefore * 60_000 }));
	const outcomeFor = (minutesBefore: number) => diagnostics.spokenAlerts.find((entry) => entry.category === "leave" && entry.minutesBefore === minutesBefore);
	const next = pending.find((entry) => entry.dueMs > nowMs && !outcomeFor(entry.minutesBefore)) ?? null;
	// The cron builds its own snapshot, so its leave time can drift from the one on screen.
	const drift = server?.leaveByMs != null && leaveByMs != null ? Math.round((server.leaveByMs - leaveByMs) / 60_000) : null;
	const diagnosticRows: Array<[string, string]> = [
		["Spoken alerts", soundEnabled ? "on" : "off — switch on Spoken alerts"],
		["Speech unlocked", diagnostics.voicePrimed ? "yes" : "no — touch the screen once"],
		["Screen held awake", diagnostics.wakeLock],
		["Screen lock error", diagnostics.wakeLockError ?? "none"],
		["Next spoken", next ? `${leadName(next.minutesBefore)} · in ${untilLabel(next.dueMs - nowMs)}` : "nothing left today"],
	];
	if (!isNativeShell()) {
		diagnosticRows.push(["Last web push seen", diagnostics.lastPush ? `${leadName(diagnostics.lastPush.minutesBefore ?? 0)} · ${relativeAge(diagnostics.lastPush.atMs, nowMs)}` : "never"]);
	}
	diagnosticRows.push(
		[
			"Engine when asked",
			diagnostics.speechQueue
				? `${diagnostics.speechQueue.paused ? "paused" : "running"} · ${diagnostics.speechQueue.speaking ? "speaking" : "idle"}${diagnostics.speechQueue.pending ? " · queue not empty" : ""} · ${diagnostics.speechQueue.voices} voices`
				: "not asked yet",
		],
		["Speech error", diagnostics.speechError ?? "none"],
	);

	return (
		<>
			<Grid rows={diagnosticRows} />
			{pending.length > 0 ? (
				<ol className="debug__ranking">
					{pending.map((entry) => (
						<li key={entry.minutesBefore}>
							<span className="debug__score">{untilLabel(entry.dueMs - nowMs)}</span>
							<span className="debug__id">{leadName(entry.minutesBefore)}</span>
							<span className="debug__status">{outcomeFor(entry.minutesBefore)?.status ?? (entry.dueMs <= nowMs ? "passed" : "pending")}</span>
						</li>
					))}
				</ol>
			) : (
				<p className="field__hint">No leave time on this trip, so nothing is scheduled to speak.</p>
			)}

			<Grid
				rows={[
					["Push alerts", server == null ? "loading…" : !server.subscribed ? "no alert saved" : server.enabled ? "on" : "off"],
					["Server leave time", server?.leaveByMs ? clock(server.leaveByMs, Intl.DateTimeFormat().resolvedOptions().timeZone) : "—"],
					["Server vs app", drift == null ? "—" : drift === 0 ? "in step" : `${drift > 0 ? "+" : ""}${drift} min`],
					["Cron awake", server == null ? "—" : server.withinWindow ? "yes" : "outside its window"],
					["Alert day", server == null ? "—" : server.alertDay ? "yes" : "not today"],
				]}
			/>
			{server?.leads && server.leads.length > 0 ? (
				<ol className="debug__ranking">
					{server.leads.map((entry) => (
						<li key={entry.minutesBefore}>
							<span className="debug__score">{entry.dueMs == null ? "—" : untilLabel(entry.dueMs - nowMs)}</span>
							<span className="debug__id">push {leadName(entry.minutesBefore)}</span>
							<span className="debug__status">{entry.sent ? "sent" : entry.dueMs != null && entry.dueMs <= nowMs ? "missed" : "pending"}</span>
						</li>
					))}
				</ol>
			) : null}
		</>
	);
}

interface AlertSchedule {
	subscribed: boolean;
	enabled?: boolean;
	alertDay?: boolean;
	withinWindow?: boolean;
	leaveByMs?: number | null;
	leads?: Array<{ minutesBefore: number; dueMs: number | null; sent: boolean }>;
}

/** Polled slowly: each call rebuilds a snapshot server-side, exactly as the cron does. */
function useAlertSchedule(deviceId: string): AlertSchedule | null {
	const [schedule, setSchedule] = useState<AlertSchedule | null>(null);

	useEffect(() => {
		let cancelled = false;
		const load = () => {
			void fetch(`/api/alerts/schedule?deviceId=${deviceId}`)
				.then((response) => (response.ok ? (response.json() as Promise<AlertSchedule>) : null))
				.then((body) => {
					if (!cancelled && body) {
						setSchedule(body);
					}
				})
				.catch(() => undefined);
		};
		load();
		const timer = window.setInterval(load, 30_000);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [deviceId]);

	return schedule;
}

function leadName(minutesBefore: number): string {
	return minutesBefore === 0 ? "at leave time" : `${minutesBefore} min before`;
}

function untilLabel(remainingMs: number): string {
	const seconds = Math.round(remainingMs / 1000);
	const sign = seconds < 0 ? "-" : "";
	const total = Math.abs(seconds);
	return `${sign}${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** A saved preset already knows its address; anything else is looked up once and cached. */
function useOriginAddress(position: { lat: number; lon: number } | null): string | null {
	const preset = matchOriginPreset(position);
	const [address, setAddress] = useState<string | null>(null);
	const lat = position?.lat;
	const lon = position?.lon;

	useEffect(() => {
		if (lat == null || lon == null || preset) {
			setAddress(null);
			return;
		}
		let cancelled = false;
		void fetch(`/api/address?lat=${lat}&lon=${lon}`)
			.then((response) => (response.ok ? (response.json() as Promise<{ address: string | null }>) : null))
			.then((body) => {
				if (!cancelled) {
					setAddress(body?.address ?? null);
				}
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [lat, lon, preset]);

	return preset?.address ?? address;
}

function toClockInput(minutesSinceMidnight: number): string {
	const hours = Math.floor(minutesSinceMidnight / 60);
	return `${String(hours).padStart(2, "0")}:${String(Math.round(minutesSinceMidnight % 60)).padStart(2, "0")}`;
}

function minutesOfDay(epochMs: number): number {
	const date = new Date(epochMs);
	return date.getHours() * 60 + date.getMinutes();
}

/** Today's date at the given wall-clock time, in the phone's own timezone. */
function epochForTodayAt(value: string): number | undefined {
	if (!value) {
		return undefined;
	}
	const [hours, mins] = value.split(":").map(Number);
	const date = new Date();
	date.setHours(hours || 0, mins || 0, 0, 0);
	return date.getTime();
}

export function DebugPanel({
	snapshot,
	nowMs,
	leads,
	leaveByMs,
	soundEnabled,
	deviceId,
	location,
}: {
	snapshot: CommuteSnapshot;
	nowMs: number;
	/** Minutes before the leave time that a reminder is due. */
	leads: number[];
	leaveByMs: number | null;
	soundEnabled: boolean;
	deviceId: string;
	location: LocationDebug;
}) {
	const debug = snapshot.debug;
	const selected = snapshot.options.find((plan) => plan.id === snapshot.recommendedId);
	const originAddress = useOriginAddress(snapshot.position);

	return (
		<section className="debug" aria-label="Debug">
			<h2>This device</h2>
			<Identity deviceId={deviceId} location={location} />

			<h2>Decision inputs</h2>

			<Grid
				rows={[
					["Now", `${clock(snapshot.nowMs, snapshot.timezone)}${snapshot.simulated ? " (simulated)" : ""}`],
					["Timezone", snapshot.timezone],
					["Profile", `${snapshot.profile.id} · ${snapshot.selection.method}${snapshot.selection.ambiguous ? " · ambiguous" : ""}`],
					["Route", `${snapshot.route.fromStationId} → ${snapshot.route.toStationId}`],
					["Origin", snapshot.position ? `${snapshot.position.lat.toFixed(5)}, ${snapshot.position.lon.toFixed(5)}` : "none"],
					["Drive", snapshot.drive ? `${minutes(snapshot.drive.durationSeconds)} min · ${snapshot.drive.source} · ${relativeAge(snapshot.drive.fetchedAtMs, nowMs)}` : "no travel leg"],
					// Coordinates cannot be checked at a glance; a wrong starting point is obvious as an address.
					["Starting from", snapshot.position ? (originAddress ?? "looking up…") : "none"],
					["Route cache", debug ? String(debug.routeCacheHit) : "—"],
					["Feed timestamp", snapshot.feed.timestampMs ? clock(snapshot.feed.timestampMs, snapshot.timezone) : "none"],
					["Feed age", snapshot.feed.ageSeconds == null ? "—" : `${snapshot.feed.ageSeconds}s${snapshot.feed.stale ? " (stale)" : ""}`],
					["Feed entities", String(snapshot.feed.entityCount)],
					["Feed error", snapshot.feed.error ?? "none"],
					["Schedule built", relativeAge(snapshot.scheduleGeneratedAtMs, nowMs)],
					["Service date", debug?.serviceDate ?? "—"],
					["Active services", debug?.activeServiceIds.join(", ") || "—"],
					["Trips matched", debug ? `${debug.realtimeTripsMatched} matched / ${debug.realtimeTripsUnmatched} unmatched` : "—"],
					["Unmatched sample", debug?.unmatchedSampleIds.join(", ") || "none"],
					["Target window", snapshot.targetWindow ? `${clock(snapshot.targetWindow.startMs, snapshot.timezone)}–${clock(snapshot.targetWindow.endMs, snapshot.timezone)} (aim ${clock(snapshot.targetWindow.targetMs, snapshot.timezone)}${snapshot.targetWindow.label ? ` at ${snapshot.targetWindow.label}` : ""})` : "none"],
					["Egress", `${Math.round(snapshot.profile.egressSeconds / 60)} min`],
					["Platform arrival", selected ? clock(selected.platformArrivalMs, snapshot.timezone) : "—"],
					["Platform wait", selected?.platformWaitSeconds == null ? "—" : `${selected.platformWaitSeconds}s`],
					["Timings", debug ? Object.entries(debug.timingsMs).map(([key, value]) => `${key} ${value}ms`).join(" · ") : "—"],
				]}
			/>

			<h3>Alerts</h3>
			<VoiceAlerts leads={leads} leaveByMs={leaveByMs} nowMs={nowMs} soundEnabled={soundEnabled} deviceId={deviceId} />

			<h3>Location</h3>
			<Grid
				rows={[
					["Watching", location.watching ? (location.pending ? "waiting for a fix" : "yes") : "no — alerts are off"],
					["Location error", location.error ?? "none"],
					["Accuracy", location.accuracyMeters == null ? "—" : `${Math.round(location.accuracyMeters)} m`],
					["Fix taken", location.fixAtMs == null ? "never" : relativeAge(location.fixAtMs, nowMs)],
					[
						"Speed",
						location.speedMetersPerSecond == null ? "not reported" : `${(location.speedMetersPerSecond * 2.237).toFixed(1)} mph`,
					],
					["On the move", location.traveling ? (location.forced ? "yes — forced in Developer" : "yes") : "no"],
					["From home", location.metersFromHome == null ? "no saved address" : `${Math.round(location.metersFromHome)} m`],
					["At a house", location.atHomeLabel ?? "neither"],
					["Drive mode", location.driving ? (location.forced ? "on — forced in Developer" : "on") : "off"],
					["Card mode", location.mode],
					["Routing from", location.routingFromCar ? "where the phone is" : "the schedule default"],
				]}
			/>

			<h3>Viewport</h3>
			<Viewport nowMs={nowMs} />

			{debug ? (
				<>
					<h3>Feeds</h3>
					<p className="debug__url">{debug.feedUrls.tripUpdates}</p>
					<p className="debug__url">{debug.feedUrls.alerts}</p>
				</>
			) : (
				<p className="field__hint">Enable debug in settings to include feed detail.</p>
			)}
		</section>
	);
}

function Grid({ rows }: { rows: Array<[string, string]> }) {
	return (
		<dl className="debug__grid">
			{rows.map(([label, value]) => (
				<div key={label}>
					<dt>{label}</dt>
					<dd>{value}</dd>
				</div>
			))}
		</dl>
	);
}
